import React from 'react';
import { useFetchItems } from '../../../../hooks/base-hooks';
import { useFetchCiscoProducts } from '../../../../hooks/product-hooks';
import { useFetchGtmArchitectures } from '../../../../hooks/gtm-solution-hooks';
import { CacheKey } from '../../../../types/cache-keys';
import { ReferenceOption } from './ReferenceSelect';

/**
 * Options for the OPTIONAL cross-links a campaign capability can carry to the wider toolbox catalogues:
 * an existing SAFE capability record (`db/capability`), a Cisco product (`db/product`), and a
 * go-to-market architecture (`db/go-to-market`). These are read-only reference lists (select an existing
 * record, no create-new) — the campaign capability stores just the chosen id in its `metadata`, so a
 * campaign play can be tied back to the canonical record it maps to without duplicating that record.
 *
 * The values are stringified ids (what `ReferenceSelect` stores); the label is the record's name with
 * its id as a hint so two same-named records stay distinguishable.
 */
export interface GlobalReferenceOptions {
  capabilityOptions: ReferenceOption[];
  productOptions: ReferenceOption[];
  goToMarketOptions: ReferenceOption[];
  loading: boolean;
}

type NamedRow = { id: number; name?: string | null };

function toOptions(rows: NamedRow[] | undefined): ReferenceOption[] {
  return (rows ?? [])
    .filter((r) => typeof r?.id === 'number')
    .map((r) => ({
      value: String(r.id),
      label: (r.name && String(r.name)) || `#${r.id}`,
      hint: r.name ? `#${r.id}` : undefined,
    }));
}

export function useGlobalReferenceOptions(): GlobalReferenceOptions {
  const { data: capabilities, isLoading: capLoading } = useFetchItems<NamedRow[]>(
    'db/capability',
    () => [CacheKey.SafeCapability],
    { size: 5000 },
  );
  const { data: products, isLoading: prodLoading } = useFetchCiscoProducts({
    vendor__is_cisco: true,
    size: 5000,
  });
  const { data: goToMarkets, isLoading: gtmLoading } = useFetchGtmArchitectures({
    type: 'architecture',
    size: 5000,
  });

  const capabilityOptions = React.useMemo(() => toOptions(capabilities), [capabilities]);
  const productOptions = React.useMemo(() => toOptions(products as NamedRow[] | undefined), [products]);
  const goToMarketOptions = React.useMemo(
    () => toOptions(goToMarkets as NamedRow[] | undefined),
    [goToMarkets],
  );

  return {
    capabilityOptions,
    productOptions,
    goToMarketOptions,
    loading: capLoading || prodLoading || gtmLoading,
  };
}
